import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getInspectionChecklist,
  getInspectionChecklistQueries,
  getInspectionFindings,
  getInspectionReviewDocuments,
} from "../../helpers/backend_helper";
import { checklistRecords } from "./checklistRecords";
import FullScreenPageLayout from "../../components/Common/FullScreenPageLayout";
import "./InspectionChecklistSelection.scss";

export default function InspectionChecklistSelection({
  review,
  breadcrumbs,
  onClose,
  onSelect,
  wizardSteps,
  discussionAction,
  headerActions,
  summaryState,
  readOnly = false,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [counts, setCounts] = useState(null);
  const [countsError, setCountsError] = useState(false);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setCounts(null);
    setCountsError(false);
    Promise.all([
      getInspectionChecklistQueries(review.id),
      getInspectionFindings(review.id),
      getInspectionReviewDocuments(review.id),
    ])
      .then(([queries, findings, documents]) => {
        if (!queries?.status || !findings?.status || !documents?.status)
          throw new Error("Unable to load counts.");
        if (active)
          setCounts({
            queries: queries.data || [],
            findings: findings.data || [],
            documents: documents.data || [],
          });
      })
      .catch(() => {
        if (active) setCountsError(true);
      });
    getInspectionChecklist(review.id)
      .then((responses) => {
        if (!responses?.status)
          throw new Error("Unable to load checklists.");
        const grouped = new Map();
        for (const question of responses.data || []) {
          const id = Number(question.checkListId);
          if (!id) continue;
          if (!grouped.has(id)) grouped.set(id, []);
          grouped.get(id).push(question);
        }
        const next = [...grouped].map(([id, questions]) => {
          const scoped = questions.filter((q) => (q.isInReviewScope ?? q.isInScope) !== false);
          const answered = scoped.filter(
            (q) =>
              Number(q.percentage) === 100 || Number(q.inspectionStatusId) === 3
          ).length;
          return {
            id,
            name: questions[0]?.checklistName || `Checklist ${id}`,
            total: scoped.length,
            answered,
            questions,
            sections: new Set(
              questions.map(
                (q) =>
                  q.checklistArea?.id ||
                  q.checklistArea?.name ||
                  q.areaName ||
                  "Unassigned"
              )
            ).size,
          };
        });
        if (active) {
          setItems(next);
          if (next.length === 1) onSelect(next[0]);
        }
      })
      .catch((reason) => {
        if (active) setError(reason.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [review.id]);
  return (
    <FullScreenPageLayout
      steps={wizardSteps}
      breadcrumbs={[
        ...breadcrumbs.map((item) => ({ label: item.label, to: item.path })),
        { label: "Checklists" },
      ]}
      showUserInfo
      showAskPiers={false}
      actions={
        headerActions || (
          <>
            {discussionAction}
            <button type="button" onClick={onClose}>
              <i className="bx bx-x" /> Close
            </button>
          </>
        )
      }
    >
      <section className="checklist-selection">
        <Link
          className="checklist-selection__back"
          to={`/inspectionreview/${review.id}`}
          state={summaryState}
        >
          <i className="bx bx-left-arrow-alt" aria-hidden="true" /> Back to
          Summary
        </Link>
        <div className="checklist-selection__heading">
          <h1 className="piers-page-title">Review checklists</h1>
          <p>
            – {review.engagementDetail?.name} ·{" "}
            {review.engagementDetail?.client?.clientName ||
              review.engagementDetail?.clientName}
          </p>
        </div>
        {loading ? (
          <p role="status">Loading checklists…</p>
        ) : error ? (
          <p role="alert">{error}</p>
        ) : items.length ? (
          items.map((item) => {
            const progress = item.total
              ? Math.round((item.answered / item.total) * 100)
              : 0;
            const records = counts
              ? checklistRecords(
                  item.questions,
                  counts.queries,
                  counts.findings,
                  []
                )
              : null;
            return (
              <article key={item.id}>
                <i className="bx bx-file" aria-hidden="true" />
                <div>
                  <h2>{item.name}</h2>
                  <small>
                    {item.sections} sections · {item.questions.length} questions · {item.total} in scope
                  </small>
                </div>
                <div
                  className="checklist-selection__counts"
                  title={
                    countsError
                      ? "Unable to load queries and findings counts"
                      : undefined
                  }
                >
                  <span>
                    <i
                      className="bx bx-message-rounded-dots"
                      aria-hidden="true"
                    />{" "}
                    Queries:{" "}
                    {records ? records.queries.length : countsError ? "—" : "…"}
                  </span>
                  <span>
                    <i className="bx bx-flag" aria-hidden="true" /> Findings:{" "}
                    {records
                      ? records.findings.length
                      : countsError
                      ? "—"
                      : "…"}
                  </span>
                </div>
                <div className="checklist-selection__progress">
                  <progress
                    value={item.answered}
                    max={item.total}
                    aria-label={`${item.name} progress`}
                  />
                  <small>
                    {item.answered} of {item.total} answered
                  </small>
                </div>
                <span>
                  {progress === 100
                    ? "Reviewed"
                    : item.answered
                    ? "In progress"
                    : "Not started"}
                </span>
                <button
                  type="button"
                  disabled={!item.questions.length}
                  onClick={() => onSelect(item)}
                >
                  {readOnly ? "View Read-Only" : !item.total ? "View checklist" : item.answered ? "Continue review" : "Review"}{" "}
                  <i className="bx bx-right-arrow-alt" />
                </button>

              </article>
            );
          })
        ) : (
          <p>No checklists have been added to this review.</p>
        )}
        <aside>
          <i className="bx bx-info-circle" /> Select a checklist to view its questions.
        </aside>
      </section>
    </FullScreenPageLayout>
  );
}
